import React, { useState, useMemo, useEffect, useCallback } from 'react';
import AddNewServiceWrapper from 'views/AddNewServices/AddSingleService/common/AddNewServiceWrapper';
import { translate } from 'components/Translation';
import LocationTab from '../tabs/LocationTab';
import LinesTab from '../tabs/LinesTab';
import _, { cloneDeep } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import SelectPlanDialog from 'views/AddNewServices/AddSingleService/tabs/common/SelectPlanDialog';
import PlansServicesTab from '../tabs/PlansServicesTab';
import Line from 'views/AddNewServices/AddSingleService/tabs/common/Line';
import PSTNTable from 'components/ServicesTables/PSTNTable';
import Summary from 'views/AddNewServices/AddSingleService/tabs/common/Summary';
import PstnFbbSummaryTab from '../tabs/PstnFbbSummaryTab';
import { ECommerceService } from 'services/eCommerce/eCommerce';
import idConfig from 'utils/ecommerce/services/id.config';
import { env } from 'config/app.config';
import { CustomAttribute, extractAttributeValue } from 'utils/ecommerce/services/extract';
import ContactDetailsTab from '../tabs/ContactDetailsTab';
import OrderTab from '../tabs/OrderTab';
import SendDialog from '../tabs/common/SendDialog';
import Phone from '@material-ui/icons/PhoneOutlined';
import { useDispatch, useSelector } from 'react-redux';
import { accountNameSelector, emailSelector } from 'redux/modules/session/selectors';
import { hideLoadingIndicator, showLoadingIndicator } from 'redux/modules/api/loadingIndicator/actions';
import { AccountsService } from 'services/accounts/accounts';
import { finalize } from 'rxjs/operators';
import FilesTab from '../tabs/FilesTab';
import { CreateHbbAccountData } from 'utils/api/payloads/omantelApi';
import { useHistory } from 'react-router-dom';
import { UsersService } from 'services/users/users';
import { MIN_LINE_NAME_LENGTH } from 'views/AddNewServices/AddSingleService/constants';
import { IAlternativeSelectItem } from 'components/AlternativeSelect/AlternativeSelect';
import { loadQualtrics } from '../../../../utils/qualtrics';

enum STEPS {
	Location = 0,
	Lines,
	PlansServices,
	Files,
	ContactDetails,
	Summary,
	Order,
}

const newLine: () => types.services.fixed.pstn.ILine = () => ({
	name: '',
	additionalServices: [],
	status: 'idle',
	id: uuidv4(),
});

interface IPSTNProps {
	back: () => void;
	mainContainerRef?: React.RefObject<HTMLDivElement>;
	account: IAlternativeSelectItem | null;
}

const PSTN = (props: IPSTNProps) => {
	const { back: handleBack, mainContainerRef } = props;
	const [activeTab, setActiveTab] = useState<STEPS>(STEPS.Location);
	const [nextStepDisabled, setNextStepDisabled] = useState<boolean>(true);
	const [gis, setGis] = useState<string>('');
	const [address, setAddress] = useState<{ [key: string]: string }>({});
	const [location, setLocation] = useState<{ [key: string]: string }>({});
	const [lines, setLines] = useState<Array<types.services.fixed.pstn.ILine>>([newLine()]);
	const [karokie, setKarokie] = useState<{ content?: string; name?: string }>({});
	const [requestId, setRequestId] = useState<string[]>([]);
	const [addPlanDialog, setAddPlanDialog] = useState<{
		show: boolean;
		ind: number;
		line?: types.services.fixed.pstn.ILine;
	}>({
		show: false,
		ind: -1,
	});

	const [contactDetails, setContactDetails] = useState<types.services.fixed.IContactDetails>({
		reqName: '',
		contactNo: '',
		bestTime: '',
		alreadyHaveNumber: false,
		workingNumber: '',
	});
	const history = useHistory();
	const [plans, setPlans] = useState<types.services.fixed.pstn.IPlan[]>([]);
	const [sendDialog, setSendDialog] = useState<boolean>(false);

	const dispatch = useDispatch();
	const email = useSelector(emailSelector);
	const accountId = props.account?.id || '';
	const accountName = useSelector(accountNameSelector);
	const sendOneRequest = async (ind: number, pipe?: Function) => {
		const line = lines[ind];
		const setStatus = (status: 'progress' | 'idle' | 'failure' | 'success') => {
			setLines((lines) => {
				const newLines = cloneDeep(lines);
				newLines[ind] = {
					...newLines[ind],
					status,
				};
				return newLines;
			});
		};

		setStatus('progress');
		dispatch(showLoadingIndicator());
		try {
			const [userProfile, accounts] = await Promise.all([
				UsersService.getUserProfile().toPromise(),
				AccountsService.getCorpAccountsForCurrentUser().toPromise(),
			]);
			return AccountsService.createHbbAccount({
				accountId,
				operationContext: 'PSTN',
				firstName: contactDetails.reqName,
				networkType: location['networkType'],
				preferredTimeToContact: contactDetails.bestTime,
				region: location['governorate'],
				cabinet: location['cabinet'],
				contactNumber1: contactDetails.contactNo,
				contactNumber2: contactDetails.workingNumber,
				dp: location['dp'],
				emailId: email,
				exe: location['exe'],
				swatSOSStatus: location['town'],
				gisDetails: gis,
				latitude: location['latitude'],
				longitude: location['longitude'],
				plansSelected: [
					line.plan!.partNumber,
					line.requiredService!.partNumber,
					...line.additionalServices.map(({ partNumber }) => partNumber),
				].join(','),
				willyat: location['willyat'],
				wayNumber: location['wayNumber'],
				buildingNumber: location['buildingNumber'],
				documents: karokie as CreateHbbAccountData['documents'],
				requestType: 'New PSTN',
				customerName: accountName,
				customerId: String(
					accounts?.accounts?.find((account) => account.accountId === userProfile.upId)?.documentId || ''
				),
			})
				.pipe(finalize(() => dispatch(hideLoadingIndicator())))
				.pipe(pipe ? pipe() : finalize(() => {}))
				.subscribe(
					(r) => {
						// @ts-ignore
						setRequestId((state) => [...state, r?.response?.createFbbAccountResponse?.requestId]);
						setStatus('success');
					},
					() => setStatus('failure')
				);
		} catch (error) {
			setStatus('failure');
		}
	};

	const handleAddKarokie = (file: { content?: string; name?: string }) => {
		setKarokie((state) => ({ ...state, ...file }));
	};

	const sendRequest = (ind = 0) => {
		if (lines.length > ind) {
			sendOneRequest(ind, () =>
				finalize(() => {
					sendRequest(ind + 1);
				})
			);
		}
	};

	useEffect(() => {
		ECommerceService.getGroupContent(idConfig.PSTN.PLANS[env]).subscribe(({ response }) =>
			setPlans(
				response.catalogEntryView.map((plan: types.ecommerce.CatalogEntryView) => ({
					name: plan.name,
					uniqueID: plan.uniqueID,
					contract: extractAttributeValue(plan, CustomAttribute.CONTRACT_DURATION),
					fee: plan.price.find((singlePrice: any) => singlePrice.usage === 'Display').value,
					offerPrice: plan.price.find((singlePrice: any) => singlePrice.usage === 'Offer').value,
					partNumber: plan.partNumber,
				}))
			)
		);
	}, []);

	const back = () => {
		setActiveTab((activeTab) => {
			switch (activeTab) {
				case STEPS.Location:
					handleBack();
					return STEPS.Location;

				case STEPS.Lines:
					return STEPS.Location;

				case STEPS.PlansServices:
					return STEPS.Lines;

				case STEPS.Files:
					return STEPS.PlansServices;

				case STEPS.ContactDetails:
					return STEPS.Files;

				case STEPS.Summary:
					return STEPS.ContactDetails;

				case STEPS.Order:
					return STEPS.Summary;

				default:
					return STEPS.Location;
			}
		});
	};

	const nextStep = () => {
		setActiveTab((activeTab) => {
			switch (activeTab) {
				case STEPS.Location:
					return STEPS.Lines;

				case STEPS.Lines:
					return STEPS.PlansServices;

				case STEPS.PlansServices:
					return STEPS.Files;

				case STEPS.Files:
					return STEPS.ContactDetails;

				case STEPS.ContactDetails:
					return STEPS.Summary;

				case STEPS.Summary:
					return STEPS.Order;

				case STEPS.Order:
					sendRequest();
					setSendDialog(true);
					return STEPS.Order;

				default:
					return STEPS.Lines;
			}
		});
	};

	const handleDeleteLine = (ind: number) =>
		setLines((lines) => {
			if (lines.length > 1) return lines.filter((_line, lineInd) => lineInd !== ind);
			else return lines;
		});

	const handleDeletePlan = (ind: number) =>
		setLines((lines) =>
			lines.map((line, lineInd) => {
				if (lineInd === ind) {
					return { ...cloneDeep(line), plan: undefined };
				}
				return line;
			})
		);

	const handleEditPlan = useCallback(
		(ind: number) =>
			setAddPlanDialog({
				ind,
				line: lines[ind],
				show: true,
			}),
		[setAddPlanDialog, lines]
	);

	const linesEl = useMemo(() => {
		if (activeTab >= STEPS.PlansServices) {
			return lines.map((line: types.services.fixed.pstn.ILine, ind: number) => ({
				lineEl: (
					<Line
						{...line}
						Icon={Phone}
						name={translate('numbers.fix-postpaid')}
						ind={ind + 1}
						iconActive={!!line.plan}
						disableEdit
						showNumber={lines.length > 1}
						minLineNameLength={MIN_LINE_NAME_LENGTH}
					/>
				),
				summaryEl: !!line.plan ? (
					<Summary
						title={line.plan!.name || ''}
						subtitle=""
						detailsTitle=""
						plan={line.plan!}
						additionalServices={line.additionalServices}
						requiredService={line.requiredService!}
						onDelete={() => handleDeletePlan(ind)}
						onEdit={() => handleEditPlan(ind)}
					/>
				) : (
					undefined
				),
			}));
		} else {
			return [];
		}
	}, [activeTab, lines, handleEditPlan]);

	const orderEl = useMemo(() => {
		if (activeTab >= STEPS.PlansServices) {
			return lines.map((line: types.services.fixed.pstn.ILine, ind: number) => ({
				lineEl: (
					<Line
						{...line}
						Icon={Phone}
						name={translate('numbers.fix-postpaid')}
						ind={ind + 1}
						disableEdit
						showNumber={lines.length > 1}
						minLineNameLength={MIN_LINE_NAME_LENGTH}
					/>
				),
				summaryEl: !!line.plan ? (
					<Summary
						showDetails
						title={line.plan!.name || ''}
						subtitle=""
						detailsTitle={translate('number.details.monthly.subscription')}
						plan={line.plan!}
						additionalServices={line.additionalServices}
						requiredService={line.requiredService!}
					/>
				) : (
					undefined
				),
			}));
		} else {
			return [];
		}
	}, [activeTab, lines]);

	useEffect(() => {
		setLines((lines) => {
			if (lines.some(({ plan }) => !!plan && !plan.fee))
				return lines.map((line) => {
					if (!!line.plan && !line.plan.fee)
						return {
							...line,
							plan: plans.find(({ uniqueID }) => uniqueID === line.plan!.uniqueID),
						};
					else return line;
				});
			else return lines;
		});
	}, [lines, plans]);

	return (
		<AddNewServiceWrapper
			width={800}
			activeTab={activeTab}
			setActiveTab={setActiveTab}
			buttons={[
				{
					color: 'tertiary-light',
					text: activeTab === STEPS.Location ? translate('cancel') : translate('back'),
					onClick: () => back(),
				},
				{
					color: 'primary-light',
					text: translate(activeTab === STEPS.Order ? 'orderNow' : 'next'),
					onClick: nextStep,
					disabled: nextStepDisabled || (activeTab === STEPS.Files && !karokie.content),
				},
			]}
			tabs={[
				{
					name: translate('add.new.line.fixed.location'),
					title: translate('add.new.line.fixed.title'),
					content: (
						<LocationTab
							setNextStepDisabled={setNextStepDisabled}
							setGis={setGis}
							setAddress={setAddress}
							address={address}
							location={location}
							setLocation={setLocation}
							setLocationInputs={(name: string, value: string) =>
								setLocation((location) => ({ ...location, [name]: value }))
							}
							mainContainerRef={mainContainerRef}
						/>
					),
				},
				{
					name: translate('add.new.line.fixed.lines'),
					title: translate('add.new.line.fixed.lines.title'),
					content: (
						<LinesTab
							lines={lines}
							linesEl={[]}
							numberOfLines={lines.length}
							setNextStepDisabled={setNextStepDisabled}
							handleIncreaseNumberOfLines={() =>
								setLines((lines) => [...lines.map((line) => _.cloneDeep(line)), newLine()])
							}
							handleDecreaseNumberOfLines={() => setLines((lines) => lines.slice(-(lines.length - 1)))}
							handleOneLine={() => setLines((lines) => [{ ..._.cloneDeep(lines[0]) }])}
							handleManyLines={() =>
								setLines((lines) => [{ ..._.cloneDeep(lines[0]) }, { ...newLine() }])
							}
							handleRemoveItem={handleDeleteLine}
						/>
					),
				},
				{
					name: translate('add.new.line.plansServices'),
					title: translate('add.new.line.plansServices.title'),
					content: (
						<>
							<PlansServicesTab
								lines={linesEl}
								setNextStepDisabled={setNextStepDisabled}
								hasPlan={(ind: number) => !!lines[ind].plan}
								handleAddPlan={(ind: number) => setAddPlanDialog({ show: true, ind })}
								handleDeletePlan={handleDeletePlan}
							/>
							<ConditionalRender
								show={addPlanDialog.show}
								onTrue={() => (
									<SelectPlanDialog
										isFbb={false}
										hideDialog={() => setAddPlanDialog({ show: false, ind: -1 })}
										TableComponent={PSTNTable}
										planTitle={translate('add.new.line.pstn.planTitle')}
										line={addPlanDialog.line}
										handleAddPlan={(
											plan: types.services.fixed.IPlan,
											additionalServices: types.services.fixed.IAdditionalService[],
											{ requiredService }
										) => {
											setAddPlanDialog((addPlanDialog) => {
												setLines((lines) => {
													const newLines = cloneDeep(lines);
													newLines[addPlanDialog.ind] = {
														...newLines[addPlanDialog.ind],
														plan,
														additionalServices,
														requiredService,
													};
													return newLines;
												});
												return {
													show: false,
													ind: -1,
												};
											});
										}}
									/>
								)}
							/>
						</>
					),
				},
				{
					name: translate('add.line.contact.files'),
					title: translate('add.line.contact.files'),
					content: <FilesTab handleAddKarokie={handleAddKarokie} karokie={karokie} />,
				},
				{
					name: translate('add.line.contact.details'),
					title: translate('add.line.contact.details'),
					content: (
						<ContactDetailsTab
							{...contactDetails}
							onChange={(name: keyof types.services.fixed.IContactDetails, value: string | boolean) =>
								setContactDetails((contactDetails) => ({ ...contactDetails, [name]: value }))
							}
							setNextStepDisabled={setNextStepDisabled}
						/>
					),
				},
				{
					name: translate('summary'),
					title: translate('summary'),
					content: (
						<PstnFbbSummaryTab
							setNextStepDisabled={setNextStepDisabled}
							location={location}
							contactDetails={contactDetails}
						/>
					),
				},
				{
					name: translate('add.new.line.fixed.order.title'),
					title: translate('add.new.line.fixed.order.subtitle'),
					content: (
						<>
							<OrderTab lines={lines} linesEl={orderEl} />
							<ConditionalRender
								show={sendDialog}
								onTrue={() => (
									<SendDialog
										hideDialog={() => {
											setSendDialog(false);
											setLines((lines) => lines.map((line) => ({ ...line, status: 'idle' })));
											history.push(`/dashboard/services`);
											loadQualtrics({
												activity_type: 'Add New Service - PSTN',
												service_fixed: lines.length,
											});
										}}
										sendRequest={(ind: number) => {
											sendOneRequest(ind);
										}}
										requestId={requestId}
										lines={orderEl.map((line, ind) => ({
											line: line.lineEl,
											status: lines[ind].status,
										}))}
									/>
								)}
							/>
						</>
					),
				},
			]}
		/>
	);
};

export default PSTN;
